import React, { forwardRef, useEffect, useState } from 'react';
import { ExternalLink, Lock, Minus, Plus, Zap } from 'lucide-react';
import { formatRupees, lotRange } from '../../utils/issueSubscription';
import { formatLongDate } from '../../utils/gmpBoard';

// Brokers' IPO sections (opened in a new tab; nothing is prefilled or shared)
const BROKERS = [
  { name: 'Zerodha', url: 'https://kite.zerodha.com/bids/ipo' },
  { name: 'Angel One', url: 'https://www.angelone.in/ipo' },
  { name: 'Groww', url: 'https://groww.in/ipo' },
];

const VPA_PATTERN = /^[\w.-]{2,}@[a-z][a-z0-9.]{1,}$/i;

/**
 * Bid planner for the issue: category, lots, price (or cut-off) and the amount
 * UPI will block. Direct mandates aren't live yet, so submitting only shows a
 * notice; the UPI ID isn't stored or sent anywhere.
 */
export const AsbaBidCard = forwardRef(({ issue, slabs, phase, onNotice }, ref) => {
  const isSme = issue.board === 'sme';
  const tiers = [
    { key: 'retail', label: isSme ? 'Individual (2 Lots)' : 'Retail (Max ₹2L)', cutOff: true },
    { key: 'shni', label: isSme ? 'HNI (3+ Lots)' : 'sHNI (₹2L – ₹10L)', cutOff: false },
  ].filter((tier) => lotRange(slabs, tier.key));
  const [tierKey, setTierKey] = useState('retail');
  const tier = tiers.find((entry) => entry.key === tierKey) || tiers[0];
  const range = tier ? lotRange(slabs, tier.key) : null;
  const [lots, setLots] = useState(range?.min || 1);
  const [cutOff, setCutOff] = useState(true);
  const [price, setPrice] = useState(issue.priceHigh ? String(issue.priceHigh) : '');
  const [vpa, setVpa] = useState('');

  const rangeMin = range?.min;
  useEffect(() => {
    if (rangeMin) setLots(rangeMin);
  }, [tierKey, rangeMin]);

  const bidPrice = tier?.cutOff && cutOff ? issue.priceHigh : Number(price);
  const priceValid = bidPrice >= (issue.priceLow || 0) && bidPrice <= (issue.priceHigh || 0);
  const amount = priceValid && issue.lotSize ? lots * issue.lotSize * bidPrice : null;
  const vpaValid = VPA_PATTERN.test(vpa.trim());
  const isOpen = phase === 'open' || phase === 'closing_today' || phase === 'upcoming';

  const handleSubmit = (event) => {
    event.preventDefault();
    setVpa('');
    onNotice(
      'Direct UPI ASBA mandates are coming soon. To bid now, use your broker’s IPO section or your bank’s ASBA net-banking with the amount shown here.'
    );
  };

  return (
    <section
      ref={ref}
      id="asba-bid"
      aria-labelledby="asba-heading"
      className="scroll-mt-24 bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 id="asba-heading" className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
          <Zap className="w-4 h-4 text-[#008080]" aria-hidden="true" />
          Direct ASBA Bidding
        </h2>
        <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-[10px] font-extrabold text-emerald-700">Zero Fee</span>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">Plan your bid: UPI blocks the amount until allotment, and unallotted funds are released.</p>

      {!isOpen || !range || !issue.lotSize || !issue.priceHigh ? (
        <p className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500">
          {isOpen
            ? 'Bid inputs open once the price band and lot size are announced.'
            : `Bidding closed${issue.closeOn ? ` on ${formatLongDate(issue.closeOn)}` : ''}. Check the allotment status with the registrar below.`}
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="mt-3 space-y-3.5">
          <fieldset>
            <legend className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Investor Category</legend>
            <div className="mt-1.5 grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100">
              {tiers.map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  aria-pressed={entry.key === tier.key}
                  onClick={() => setTierKey(entry.key)}
                  className={`px-2 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${
                    entry.key === tier.key ? 'bg-[#008080] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          </fieldset>

          <div>
            <p className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <span id="asba-lots-label">Bid Quantity (1 Lot = {issue.lotSize.toLocaleString('en-IN')} Shares)</span>
              <span className="normal-case tracking-normal text-slate-700">{(lots * issue.lotSize).toLocaleString('en-IN')} shares</span>
            </p>
            <div className="mt-1.5 flex items-center gap-2" role="group" aria-labelledby="asba-lots-label">
              <button
                type="button"
                onClick={() => setLots((value) => Math.max(range.min, value - 1))}
                disabled={lots <= range.min}
                aria-label="Fewer lots"
                className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center"
              >
                <Minus className="w-4 h-4" />
              </button>
              <output className="flex-1 h-9 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center text-sm font-extrabold text-slate-900 tabular-nums">
                {lots} {lots === 1 ? 'lot' : 'lots'}
              </output>
              <button
                type="button"
                onClick={() => setLots((value) => Math.min(range.max, value + 1))}
                disabled={lots >= range.max}
                aria-label="More lots"
                className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-1 text-[10px] text-slate-400">
              {range.min === range.max ? `${range.min} lots for this category` : `${range.min} – ${range.max} lots for this category`}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between gap-2">
              <label htmlFor="asba-price" className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Bid Price (Band ₹{issue.priceLow} – ₹{issue.priceHigh})
              </label>
              {tier.cutOff && (
                <label className="flex items-center gap-1 text-[11px] font-semibold text-[#008080]">
                  <input type="checkbox" checked={cutOff} onChange={(e) => setCutOff(e.target.checked)} className="accent-[#008080]" />
                  Cut-off
                </label>
              )}
            </div>
            <input
              id="asba-price"
              type="number"
              inputMode="decimal"
              min={issue.priceLow}
              max={issue.priceHigh}
              value={tier.cutOff && cutOff ? issue.priceHigh : price}
              disabled={tier.cutOff && cutOff}
              onChange={(e) => setPrice(e.target.value)}
              aria-invalid={!priceValid}
              className="mt-1.5 w-full h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-900 tabular-nums focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080] disabled:bg-slate-50 disabled:text-slate-500"
            />
            {!priceValid && <p className="mt-1 text-[11px] text-rose-600">Enter a price within the band.</p>}
          </div>

          <div>
            <label htmlFor="asba-vpa" className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              UPI ID (Virtual Payment Address)
            </label>
            <input
              id="asba-vpa"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={vpa}
              onChange={(e) => setVpa(e.target.value)}
              placeholder="username@okhdfcbank"
              className="mt-1.5 w-full h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]"
            />
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Or apply directly with one click</p>
            <ul className="mt-1.5 grid grid-cols-3 gap-1.5">
              {BROKERS.map((broker) => (
                <li key={broker.name}>
                  <a
                    href={broker.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1 h-9 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-700 hover:border-teal-200 hover:text-[#008080]"
                  >
                    {broker.name}
                    <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between rounded-xl bg-slate-900 text-white px-3.5 py-2.5">
            <span className="text-[11px] font-semibold text-slate-300">Total Block Amount</span>
            <span className="text-lg font-extrabold tabular-nums">{formatRupees(amount)}</span>
          </div>

          <button
            type="submit"
            disabled={!priceValid || !vpaValid}
            className="w-full inline-flex items-center justify-center gap-1.5 h-10 rounded-xl bg-[#008080] hover:bg-[#006666] text-sm font-extrabold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Lock className="w-4 h-4" />
            Submit ASBA Mandate
          </button>
          {!vpaValid && vpa && <p className="-mt-2 text-[11px] text-rose-600">Enter a UPI ID like name@bank.</p>}
        </form>
      )}
    </section>
  );
});

AsbaBidCard.displayName = 'AsbaBidCard';

export default AsbaBidCard;
